import data from "../data/localities.json";
import { candleCustomFor, ELEVATION_SHKIA_ELIGIBLE, type CandleCustom } from "./customs";

export * from "./customs";

/** One locality row as generated by tools/build-locations from the CBS file. */
export interface LocalityRecord {
  /** CBS locality code (סמל יישוב). */
  code: number;
  nameHe: string;
  nameEn: string | null;
  latitude: number;
  longitude: number;
  /** CBS average elevation in meters, when published. */
  elevation: number | null;
  population: number | null;
}

export interface Locality extends LocalityRecord {
  /** URL segment: the English name, or the CBS code when there is none or it is ambiguous. */
  slug: string;
  candle: CandleCustom;
  elevationShkiaEligible: boolean;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const records = data.localities as LocalityRecord[];
const slugCounts = new Map<string, number>();
for (const r of records) {
  const base = r.nameEn ? slugify(r.nameEn) : "";
  if (base) slugCounts.set(base, (slugCounts.get(base) ?? 0) + 1);
}

function slugFor(record: LocalityRecord): string {
  const base = record.nameEn ? slugify(record.nameEn) : "";
  if (!base) return String(record.code);
  return slugCounts.get(base) === 1 ? base : `${base}-${record.code}`;
}

/** All localities, most populous first. */
export const LOCALITIES: readonly Locality[] = records.map((r) => ({
  ...r,
  slug: slugFor(r),
  candle: candleCustomFor(r.code),
  elevationShkiaEligible: ELEVATION_SHKIA_ELIGIBLE.has(r.code),
}));

export const LOCALITIES_SOURCE = data.source;

const byCode = new Map(LOCALITIES.map((l) => [l.code, l]));
const bySlug = new Map(LOCALITIES.map((l) => [l.slug, l]));

export function findLocality(code: number): Locality | undefined {
  return byCode.get(code);
}

export function findLocalityBySlug(slug: string): Locality | undefined {
  return bySlug.get(slug);
}

/**
 * Ignores geresh, gershayim, quotes, hyphens, spacing and Latin case, and treats full and
 * defective Hebrew spelling alike (קריית/קרית, תקווה/תקוה).
 */
export function normalizeName(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-–—]/g, " ")
    .replace(/["'`׳״()*.]/g, "")
    .replace(/וו/g, "ו")
    .replace(/יי/g, "י")
    .replace(/\s+/g, " ")
    .trim();
}

const searchKeys = LOCALITIES.map((l) => ({
  locality: l,
  keys: [l.nameHe, l.nameEn ?? ""].filter(Boolean).map(normalizeName),
}));

/** Prefix matches first, then substring matches; each group most populous first. */
export function searchLocalities(query: string, limit = 10): Locality[] {
  const q = normalizeName(query);
  if (!q) return [];
  const prefix: Locality[] = [];
  const contains: Locality[] = [];
  for (const { locality, keys } of searchKeys) {
    if (keys.some((k) => k.startsWith(q) || k.includes(` ${q}`))) prefix.push(locality);
    else if (keys.some((k) => k.includes(q))) contains.push(locality);
  }
  return [...prefix, ...contains].slice(0, limit);
}

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(a));
}

export function nearestLocality(
  latitude: number,
  longitude: number,
): { locality: Locality; distanceKm: number } | undefined {
  let best: { locality: Locality; distanceKm: number } | undefined;
  for (const locality of LOCALITIES) {
    const d = distanceKm(latitude, longitude, locality.latitude, locality.longitude);
    if (!best || d < best.distanceKm) best = { locality, distanceKm: d };
  }
  return best;
}
